import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import Box from "@mui/material/Box";
import { FormControl, InputLabel, MenuItem, OutlinedInput, Select } from "@mui/material";

import npiProfileMasterService from "@plannel/services/data/npi-profile-master-service";
import customerService from "@plannel/services/data/customer-service";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import i18n from "@plannel/utils/i18n";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import PaginationContainer from "@plannel/components/PaginationContainer";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import ResponseUtil from "../utils/response-util";

const { snackBarMessageColor } = ColorUtil;
const { httpStatusCode, responseCode } = ResponseUtil;

/* 계산 방법 / 반영 기준 — NpiHistory 화면과 같은 코드값을 쓴다 */
const ACTION_CDS = ["sum", "avg"];
const APPLY_TYPES = ["ACTUAL", "PLAN"];
const ALL = "ALL";

const REQUIRED_FIELDS = [
  { field: "itemNodeCd", label: "npiCode" },
  { field: "customerNodeCd", label: "customerCd" },
  { field: "likeItemNodeCd", label: "likeItemCd" },
  { field: "likeCustomerNodeCd", label: "likeCustomerCd" },
  { field: "actionCd", label: "computeMethod" },
  { field: "applyType", label: "applyType" },
  { field: "weightRate", label: "weight" },
];

const NpiProfileMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [tPages, setTotalPages] = useState(0);
  const [sortParams, setSortParams] = useState({});

  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const itemHrchySearch = useRef(reduxViewState?.itemHrchySearch ?? "");
  const custHrchySearch = useRef(reduxViewState?.custHrchySearch ?? "");
  const [applyType, setApplyType] = useState(reduxViewState?.applyType ?? ALL);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ?? null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [npiProfiles, setNpiProfiles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [custHrchys, setCustHrchys] = useState([]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * 거래처 셀은 SalesPlan 마스터처럼 계층 여부에 따라 다른 목록을 띄운다.
   * 거래처 계층 = false -> 거래처(z_customer) 목록, true -> 거래처 계층(z_customer_hrchy) 목록.
   * 계층 목록도 customerCd 로 코드를 맞춰 두어(retrieveCustHrchys) 한 가지 접근자로 처리한다.
   */
  const customerEditorParams = hrchyFlgField => params => ({
    values: params?.data?.[hrchyFlgField] ? custHrchys : customers,
    formatValue: value => value?.name ?? ""
  });

  /*
   * 셀 값은 거래처명(문자열) 그대로 두고 목록만 객체로 띄운다.
   * (export 는 valueGetter 가 있으면 원본 값을 그대로 내보내므로 — GridExportSetting.processExportCell —
   *  셀 값을 객체로 두면 엑셀에 [object Object] 가 찍힌다)
   */
  const setCustomerCell = (cdField, nameField) => params => {
    const selected = params.newValue;
    if (!selected || typeof selected !== "object") {
      return false;
    }

    params.data[cdField] = selected.customerCd ?? "";
    params.data[nameField] = selected.name ?? "";
    return true;
  };

  const columnDefs = [
    { headerName: "npiCode", field: "itemNodeCd", filterName: "itemNodeCd", minWidth: 150, flex: 2, cellClass: "stringType", filterType: "string" },
    { headerName: "npi", field: "itemNodeName", filterName: "itemNodeNm", type: ["nonEditableColumn"], minWidth: 200, flex: 3, filterType: "string" },
    {
      headerName: "itemHrchyFlg",
      field: "itemHrchyFlg",
      width: 130,
      type: ["booleanColumn", "nonEditableColumn"],
      filterType: 'boolean'
    },
    { headerName: "customerCd", field: "customerNodeCd", filterName: "customerNodeCd", type: ["nonEditableColumn"], minWidth: 150, flex: 2, cellClass: "stringType", filterType: "string" },
    {
      headerName: "customerName", field: "customerNodeName", filterName: "customerNodeNm",
      minWidth: 200, flex: 3, filterType: "string",
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: customerEditorParams("customerHrchyFlg"),
      /* 에디터가 돌려주는 값은 거래처/계층 객체다. 셀 값은 문자열이라 AG Grid가 컬럼 타입을
         text로 추론해 객체 newValue를 타입 불일치로 반려한다(warning #135) — valueSetter가
         직접 처리하므로 자동 타입 추론/검증을 끈다 */
      cellDataType: false,
      valueSetter: setCustomerCell("customerNodeCd", "customerNodeName")
    },
    {
      /* 이 체크로 거래처 셀에 뜨는 목록(거래처 / 거래처 계층)이 결정되므로 편집 가능하게 둔다 */
      headerName: "customerHrchyFlg", field: "customerHrchyFlg", width: 140, type: ["booleanColumn"], filterType: "boolean",
      valueSetter: params => {
        const newValue = !!params.newValue;
        if (params.data.customerHrchyFlg === newValue) {
          return false;
        }

        params.data.customerHrchyFlg = newValue;
        /* 레벨이 바뀌면 기존에 고른 거래처는 더 이상 유효하지 않다 */
        params.data.customerNodeCd = "";
        params.data.customerNodeName = "";
        params.data.likeCustomerNodeCd = "";
        params.data.likeCustomerNodeName = "";
        return true;
      }
    },
    { headerName: "likeItemCd", field: "likeItemNodeCd", filterName: "likeItemNodeCd", minWidth: 150, flex: 2, cellClass: "stringType", filterType: "string" },
    { headerName: "likeItemName", field: "likeItemNodeName", filterName: "likeItemNodeNm", type: ["nonEditableColumn"], minWidth: 200, flex: 3, filterType: "string" },
    { headerName: "likeCustomerCd", field: "likeCustomerNodeCd", filterName: "likeCustomerNodeCd", type: ["nonEditableColumn"], minWidth: 150, flex: 2, cellClass: "stringType", filterType: "string" },
    {
      headerName: "likeCustomerName", field: "likeCustomerNodeName", filterName: "likeCustomerNodeNm",
      minWidth: 200, flex: 3, filterType: "string",
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellDataType: false,
      cellEditorParams: customerEditorParams("customerHrchyFlg"),
      valueSetter: setCustomerCell("likeCustomerNodeCd", "likeCustomerNodeName")
    },
    /* 계층 여부는 코드로 서버가 판별해 저장하므로 읽기 전용으로만 보여준다 (SalesPlan 마스터와 동일) */
    {
      headerName: "computeMethod", field: "actionCd", filterName: "actionCd", width: 130, filterType: "string",
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true, type: ["defaultValueParser"], filterOption: ACTION_CDS,
      cellEditorParams: { values: ACTION_CDS },
      valueFormatter: params => (params.value === ACTION_CDS[0] ? t("sumLabel") : params.value ? t(params.value) : "")
    },
    {
      headerName: "applyType", field: "applyType", width: 130, filterType: "string", filterOption: APPLY_TYPES,
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true, type: ["defaultValueParser"],
      cellEditorParams: { values: APPLY_TYPES },
      valueFormatter: params => (params.value ? t(params.value === "PLAN" ? "planApply" : "actualApply") : "")
    },
    {
      ...GridUtils.composedHeader(() => `${i18n.t("weight")} (%)`), field: "weightRate", colId: "weightRate", filterName: "weightRate",
      cellEditor: NumericEditor, type: ["numberColumn", "rightAligned"], width: 130, filterType: "number"
    },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 3, filterType: "timestamp" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 3, filterType: "timestamp" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: ["nonEditableColumn"], width: 150, filterType: "string" }
  ];

  const getRequestParams = () => {
    const searchFilters = {};

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch.current) {
      searchFilters.customer = customerSearch.current;
    }

    if (itemHrchySearch.current) {
      searchFilters.itemHrchyCd = itemHrchySearch.current;
    }
    if (custHrchySearch.current) {
      searchFilters.custHrchyCd = custHrchySearch.current;
    }
    if (applyType !== ALL) {
      searchFilters.applyType = applyType;
    }

    const params = { searchFilters };
    if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      params.advancedFilters = advancedFilters;
    }
    if (currentPage) {
      params.page = currentPage - 1;
    }
    if (pageSize) {
      params.pageSize = pageSize;
    }

    return { ...params, ...sortParams };
  };

  const retrieveNpiProfiles = () => {
    npiProfileMasterService.getAll(getRequestParams())
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setNpiProfiles(results ?? []);
        setTotalPages(totalPages ?? 0);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        DataState.initialize(gridRef.current?.api);

        const reduxState = reduxUtil.createObjectState(viewName, {
          itemSearch: itemSearch.current, customerSearch: customerSearch.current,
          extraItemCodes: extraItemCodesRef.current, extraCustomerCodes: extraCustomerCodesRef.current,
          itemHrchySearch: itemHrchySearch.current, custHrchySearch: custHrchySearch.current,
          applyType, currentPage, pageSize, advancedFilters
        });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      });
  };

  const retrieveCustomers = () => {
    customerService.getLookup()
      .then(response => {
        setCustomers(response.data?.results ?? []);
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveCustHrchys = () => {
    customerHrchyService.getAll()
      .then(response => {
        /* 계층 목록도 거래처와 같은 접근자(customerCd)로 쓰도록 코드를 맞춰 둔다 */
        setCustHrchys((response.data?.results ?? []).map(result => ({ ...result, customerCd: result.hrchyCd })));
      })
      .catch(e => {
        debug(e);
      });
  };

  const onGridReady = params => {
    retrieveCustomers();
    retrieveCustHrchys();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const saveNpiProfiles = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (isEmpty(allStateData)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    const missingLabels = new Set();
    allStateData.forEach(row => {
      REQUIRED_FIELDS.forEach(({ field, label }) => {
        const value = row[field];
        if (value === null || value === undefined || value === "") {
          missingLabels.add(t(label));
        }
      });
    });

    if (missingLabels.size > 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: [...missingLabels].join("/") }) });
      return;
    }

    npiProfileMasterService.upsert(allStateData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        retrieveNpiProfiles();
      })
      .catch(e => {
        const status = e.response?.status;
        const errorDatas = e.response?.data;

        // 코드 검증 실패는 { notFoundItem: "코드" } / { notFoundCustomer: "코드" } 형태로 온다
        if (status === httpStatusCode.BadRequest && errorDatas) {
          const errorCode = Object.keys(errorDatas)[0];
          if (errorCode === responseCode.NOT_FOUND_ITEM) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NotExistItemCodes", { itemCodes: errorDatas[errorCode] }) });
          } else if (errorCode === responseCode.NOT_FOUND_CUSTOMER) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NotExistCustomerCodes", { customerCodes: errorDatas[errorCode] }) });
          } else if (errorCode === responseCode.MISMATCH_NODE_LEVEL) {
            // 신제품과 참조 대상의 레벨(계층/leaf)이 다른 경우
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_MismatchNodeLevel", { codes: errorDatas[errorCode] }) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        }
        debug(e);
      });
  };

  const removeNpiProfiles = () => {
    gridRef.current.api.stopEditing();
    const ids = gridRef.current.api.getSelectedRows().map(data => data.id).filter(Boolean);

    if (isEmpty(ids)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    npiProfileMasterService.remove(ids)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        debug(e);
      })
      .then(() => {
        retrieveNpiProfiles();
      });
  };

  const addRow = () => {
    const newRow = {
      id: null,
      itemNodeCd: "",
      customerNodeCd: "",
      likeItemNodeCd: "",
      likeCustomerNodeCd: "",
      itemHrchyFlg: false,
      customerHrchyFlg: false,
      actionCd: ACTION_CDS[0],
      applyType: applyType === ALL ? APPLY_TYPES[0] : applyType,
      weightRate: 100
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const handleDialog = action => {
    if (action === "SAVE") {
      saveNpiProfiles();
    } else if (action === "DELETE") {
      removeNpiProfiles();
    }
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveNpiProfiles();
    }
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveNpiProfiles();
    }

    return () => {
      setNpiProfiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams, applyType]);

  /* advanced filter 적용 시에는 첫 페이지부터 다시 조회한다 */
  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  useEffect(() => {
    setAdvancedFilterColor(!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0 ? "info" : "default");
  }, [advancedFilters]);

  /* 거래처/거래처 계층 목록이 채워지면 셀 에디터가 최신 목록을 쓰도록 컬럼을 다시 반영한다 */
  useEffect(() => {
    if (!isEmpty(customers) || !isEmpty(custHrchys)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, custHrchys]);

  return (
    <>
      <FilterContainer>
        {/* 250 = 이 레포에서 가장 흔한 검색창 폭. 200 이면 "Customer Hierarchy Code" 가 말줄임된다
            (겹침 자체는 TextFieldSearch 가 막지만, 잘리지 않는 게 낫다) */}
        <TextFieldSearch sx={{ mr: 1.5, width: 250 }} label={"itemHrchyCd"} searchCode={itemHrchySearch} retrieve={initPageToRetrieve} />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} sx={{ mr: 1.5 }} extraCodesRef={extraItemCodesRef} />
        <TextFieldSearch sx={{ mr: 1.5, width: 250 }} label={"customerHrchyCd"} searchCode={custHrchySearch} retrieve={initPageToRetrieve} />
        <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} useAccessFilter={false} sx={{ mr: 1.5 }} extraCodesRef={extraCustomerCodesRef} />
        <FormControl sx={{ width: 140, minWidth: 140 }}>
          <InputLabel id="npi-profile-apply-type-label">{t("applyType")}</InputLabel>
          <Select
            labelId="npi-profile-apply-type-label"
            id="npi-profile-apply-type"
            size="small"
            variant="outlined"
            value={applyType}
            onChange={event => setApplyType(event.target.value)}
            input={<OutlinedInput label={t("applyType")} />}
          >
            <MenuItem value={ALL}>{t("all")}</MenuItem>
            {APPLY_TYPES.map(type => (
              <MenuItem key={type} value={type}>{t(type === "PLAN" ? "planApply" : "actualApply")}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={npiProfileMasterService} columnDefs={columnDefs}
            params={getRequestParams()} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "row", height: "100%", mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={npiProfiles}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              enterNavigatesVertically
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(NpiProfileMaster);
